import { ApolloClient, InMemoryCache, NormalizedCacheObject, createHttpLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { gestisciRispostaDipendente } from './fineSessioneDipendente';

/**
 * Identità nei sondaggi del sito.
 *
 * I sondaggi con identità verificata accettano solo:
 *  - il Bearer associato (token in `localStorage['associate_token']`) per i
 *    sondaggi degli studi, il Bearer rappresentante
 *    (`localStorage['representative_token']`) per quelli dei rappresentanti, oppure
 *  - il link firmato ricevuto per email (`#lt=<token>` nel frammento), passato
 *    come variabile `linkToken` a OGNI operazione del sondaggio.
 *
 * Il link si legge una volta, si salva in `sessionStorage['survey_link_<id>']`
 * e si toglie dall'indirizzo (non resta nella cronologia né nei link copiati).
 */

const LINK_PREFIX = 'survey_link_';

export const surveyLinkKey = (surveyId: string) => `${LINK_PREFIX}${surveyId}`;

/** Token `lt` dal frammento dell'indirizzo (`#lt=abc` o `#x=1&lt=abc`); vuoto → null. */
export function parseLinkTokenFromHash(hash: string): string | null {
  const testo = (hash || '').replace(/^#/, '');
  if (!testo) return null;
  for (const parte of testo.split('&')) {
    const uguale = parte.indexOf('=');
    if (uguale < 0) continue;
    if (parte.slice(0, uguale) !== 'lt') continue;
    const valore = parte.slice(uguale + 1);
    if (!valore) return null;
    try {
      return decodeURIComponent(valore);
    } catch {
      return valore;
    }
  }
  return null;
}

/**
 * Link firmato del sondaggio `surveyId`: dal frammento (lo salva in
 * sessionStorage e ripulisce l'indirizzo) o, se il frammento non c'è, da
 * sessionStorage. Nessun link → null.
 */
export function getLinkTokenFromHash(surveyId: string): string | null {
  const dalFrammento = typeof window !== 'undefined' ? parseLinkTokenFromHash(window.location.hash) : null;
  if (dalFrammento) {
    try {
      window.sessionStorage.setItem(surveyLinkKey(surveyId), dalFrammento);
    } catch {
      // sessionStorage non disponibile: il token vale per questa pagina
    }
    try {
      const { pathname, search } = window.location;
      window.history.replaceState(window.history.state, '', `${pathname}${search}`);
    } catch {
      // indirizzo non modificabile: nessun effetto sul funzionamento
    }
    return dalFrammento;
  }
  try {
    return window.sessionStorage.getItem(surveyLinkKey(surveyId)) || null;
  } catch {
    return null;
  }
}

/** Dimentica il link del sondaggio (scaduto o non valido). */
export function dimenticaLinkToken(surveyId: string): void {
  try {
    window.sessionStorage.removeItem(surveyLinkKey(surveyId));
  } catch {
    // niente da fare
  }
}

function payloadJwt(token: string): Record<string, unknown> | null {
  const parti = token.split('.');
  if (parti.length !== 3) return null;
  try {
    const base64 = parti[1].replace(/-/g, '+').replace(/_/g, '/');
    const conPadding = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(conPadding));
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

/** Token in `localStorage[chiave]` se è un JWT leggibile e non scaduto, altrimenti null. */
function tokenValido(chiave: string, nowMs: number): string | null {
  let token: string | null = null;
  try {
    token = window.localStorage.getItem(chiave);
  } catch {
    return null;
  }
  if (!token) return null;
  const payload = payloadJwt(token);
  if (!payload) return null;
  if (typeof payload.exp === 'number' && payload.exp * 1000 <= nowMs) return null;
  return token;
}

/**
 * Token associato valido (non scaduto) o null. Un token scaduto non si manda:
 * il server risponderebbe 401 a tutta l'operazione.
 */
export function getAssociateToken(nowMs: number = Date.now()): string | null {
  return tokenValido('associate_token', nowMs);
}

/** Token del rappresentante (area associativa) valido o null, come `getAssociateToken`. */
export function getRepresentativeToken(nowMs: number = Date.now()): string | null {
  return tokenValido('representative_token', nowMs);
}

/**
 * Area da cui si compila il sondaggio: `riservata` (sondaggi per gli studi:
 * Bearer associato o link firmato per impresa) o `associativa` (sondaggi per i
 * rappresentanti: Bearer rappresentante o link firmato per rappresentante).
 */
export type AreaSondaggi = 'riservata' | 'associativa';

export interface PercorsiArea {
  elenco: string;
  home: string;
  /** Pagina di compilazione per chi ha già effettuato l'accesso (destinazione dopo il login) */
  compila: (id: string) => string;
  /** Pagina pubblica del link ricevuto per email (`#lt=`), aperta anche senza accesso */
  link: (id: string) => string;
  /** Risposte inviate (sola lettura), aperte dai sondaggi completati dell'elenco */
  risposte: (id: string) => string;
}

/** Percorsi di navigazione della pagina di risposta nell'area indicata. */
export function percorsiArea(area: AreaSondaggi): PercorsiArea {
  if (area === 'associativa') {
    return {
      elenco: '/area-associativa/sondaggi',
      home: '/area-associativa',
      compila: (id: string) => `/area-associativa/sondaggi/${id}`,
      link: (id: string) => `/sondaggi-rappresentanti/${id}`,
      risposte: (id: string) => `/area-associativa/sondaggi/${id}/risposte`,
    };
  }
  return {
    elenco: '/area-riservata/sondaggi',
    home: '/area-riservata',
    compila: (id: string) => `/sondaggi/${id}`,
    link: (id: string) => `/sondaggi/${id}`,
    risposte: (id: string) => `/area-riservata/sondaggi/${id}/risposte`,
  };
}

export interface SurveyOpContext {
  headers?: { Authorization: string };
  linkToken?: string;
}

/**
 * Identità da allegare a ogni operazione del sondaggio: `headers` va nel
 * `context` dell'operazione (client `getSurveyClient()`), `linkToken` tra le variabili.
 * Stessa logica nelle due aree, cambia solo il token: associato nell'area
 * riservata, rappresentante nell'area associativa. Il link firmato (`#lt=` o
 * sessionStorage `survey_link_<id>`) si legge sempre e viaggia insieme al Bearer:
 * il server usa il Bearer se c'è, altrimenti il link.
 */
export function surveyOpContext(
  surveyId: string,
  { area = 'riservata' }: { area?: AreaSondaggi } = {}
): SurveyOpContext {
  const ctx: SurveyOpContext = {};
  const token = area === 'associativa' ? getRepresentativeToken() : getAssociateToken();
  if (token) ctx.headers = { Authorization: `Bearer ${token}` };
  const linkToken = getLinkTokenFromHash(surveyId);
  if (linkToken) ctx.linkToken = linkToken;
  return ctx;
}

/** Codice (`extensions.code`) del primo errore GraphQL di un ApolloError, se c'è. */
export function codiceErrore(error: unknown): string | undefined {
  const errori = (error as { graphQLErrors?: Array<{ extensions?: { code?: unknown } }> } | null)?.graphQLErrors;
  if (!Array.isArray(errori)) return undefined;
  for (const e of errori) {
    const code = e?.extensions?.code;
    if (typeof code === 'string' && code) return code;
  }
  return undefined;
}

/**
 * Client Apollo dedicato alle operazioni di risposta ai sondaggi.
 *
 * Il client globale (`apolloClient.ts`) passa da `authLink`, che riscrive
 * sempre l'header `authorization` con il token admin (o con una stringa
 * vuota): un header impostato nel `context` dell'operazione verrebbe perso.
 * Questo client manda solo gli header del `context` (il Bearer associato).
 */
let surveyClient: ApolloClient<NormalizedCacheObject> | null = null;

/**
 * Dipendente dello studio (Ruling R15/R16): il contesto GraphQL rifiuta il suo Bearer con HTTP 401
 * (`DIPENDENTI_SPENTI`, `SESSIONE_NON_VALIDA`) o 403 (`STUDIO_NON_IN_REGOLA`) e `extensions.code`
 * nel corpo. Lo stesso trattamento delle risposte REST; per il titolare nessun effetto.
 */
export function segnalaErroreRete(networkError: unknown, autorizzazione?: string | null): void {
  const errore = networkError as { statusCode?: unknown; result?: unknown } | null | undefined;
  if (!errore || typeof errore.statusCode !== 'number') return;
  gestisciRispostaDipendente(errore.statusCode, errore.result, autorizzazione);
}

/** Authorization mandata dall'operazione (header del `context`), null se non c'è. */
const autorizzazioneOperazione = (contesto: { headers?: Record<string, unknown> } | undefined): string | null => {
  const valore = contesto?.headers?.Authorization ?? contesto?.headers?.authorization;
  return typeof valore === 'string' && valore ? valore : null;
};

export function getSurveyClient(): ApolloClient<NormalizedCacheObject> {
  if (!surveyClient) {
    surveyClient = new ApolloClient({
      link: from([
        onError(({ networkError, operation }) => segnalaErroreRete(networkError, autorizzazioneOperazione(operation?.getContext?.()))),
        createHttpLink({ uri: process.env.REACT_APP_API_URL || 'http://localhost:5000/graphql' }),
      ]),
      cache: new InMemoryCache(),
      defaultOptions: {
        query: { fetchPolicy: 'network-only' },
        watchQuery: { fetchPolicy: 'network-only' },
      },
    });
  }
  return surveyClient;
}
